import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Mail } from "lucide-react";
import { MaskedLines, Reveal, EASE } from "@/lib/motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CHALLENGE_OPTIONS } from "@/data/site";
import { SEO, breadcrumbSchema, SITE_URL, SITE_NAME } from "@/components/site/SEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Contact", path: "/contact" },
];

const CONTACT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: `${SITE_URL}/contact`,
  mainEntity: {
    "@type": "Organization",
    name: SITE_NAME,
    email: "info@getupword.com",
    contactPoint: [{ "@type": "ContactPoint", email: "info@getupword.com", contactType: "sales" }],
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", website: "", challenge: "", message: "" };

export default function Contact() {
  const [intent, setIntent] = useState("sample"); // "sample" | "general"
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, { ...form, intent });
      setDone(true);
      toast.success(data.message || "Thanks — we'll be in touch.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@getupword.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <SEO
        title="Contact — Request a Content Audit"
        description="Tell us where your content is stuck. We'll review your site and send a detailed audit within 2 business days. Based in Bengaluru, India — working with B2B SaaS teams worldwide."
        path="/contact"
        jsonLd={[breadcrumbSchema(CRUMBS), CONTACT_JSONLD]}
      />
      <Breadcrumbs items={CRUMBS} />
      <section className="pt-6 pb-16 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.25em] text-[#999999] mb-8 font-mono">Contact</p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight text-[#111111]">
            <MaskedLines lines={["Let's build your"]} delay={0.1} />
            <span className="reveal-line">
              <motion.span className="block italic" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, ease: EASE, delay: 0.22 }}>
                acquisition engine.
              </motion.span>
            </span>
          </h1>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl text-[#111111] leading-tight">Say hello.</h2>
              <p className="mt-4 text-lg text-[#666666] leading-relaxed">
                Tell us where your content is stuck. We&apos;ll review your site and send a detailed audit —
                real timelines, real pricing, no sales runaround.
              </p>
              <a href="mailto:info@getupword.com" data-testid="contact-email-link" className="inline-flex items-center gap-3 mt-10 font-serif text-2xl md:text-3xl text-[#111111] hover:text-[#666666] transition-colors">
                <Mail size={22} /> info@getupword.com
              </a>
              <div className="mt-10 pt-8 border-t border-[#E0E0E0]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#999999] mb-2">Based in</p>
                <p className="text-[#111111] text-lg">Bengaluru, India — working with B2B SaaS teams worldwide.</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="contact-success"
                className="border border-[#111111] p-10 md:p-14"
              >
                <div className="w-14 h-14 bg-[#111111] text-white flex items-center justify-center">
                  <Check size={26} />
                </div>
                <h2 className="mt-8 font-serif text-3xl md:text-4xl text-[#111111]">Thanks!</h2>
                <p className="mt-4 text-lg text-[#666666] leading-relaxed">
                  We&apos;ll review your site and send you a detailed audit within 2 business days.
                </p>
              </motion.div>
            ) : (
              <Reveal>
                <h2 className="font-serif text-3xl md:text-4xl text-[#111111] leading-tight mb-8">Start the conversation.</h2>
                <div className="inline-flex border border-[#111111] p-1 mb-10" data-testid="intent-toggle">
                  {[
                    ["sample", "Request a Sample Article"],
                    ["general", "General Inquiry"],
                  ].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`intent-${val}`}
                      onClick={() => setIntent(val)}
                      className={`relative px-5 md:px-7 py-3 text-xs md:text-sm font-semibold uppercase tracking-[0.1em] transition-colors ${
                        intent === val ? "text-white" : "text-[#111111] hover:text-[#666666]"
                      }`}
                    >
                      {intent === val && (
                        <motion.span layoutId="intent-pill" className="absolute inset-0 bg-[#111111]" transition={{ type: "spring", stiffness: 400, damping: 34 }} />
                      )}
                      <span className="relative z-10">{label}</span>
                    </button>
                  ))}
                </div>

                <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] text-[#999999]">Name *</label>
                      <input data-testid="input-name" className="editorial-input mt-2" value={form.name} onChange={set("name")} placeholder="Your name" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] text-[#999999]">Email *</label>
                      <input data-testid="input-email" type="email" className="editorial-input mt-2" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] text-[#999999]">Company</label>
                      <input data-testid="input-company" className="editorial-input mt-2" value={form.company} onChange={set("company")} placeholder="Company name" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] text-[#999999]">Website</label>
                      <input data-testid="input-website" className="editorial-input mt-2" value={form.website} onChange={set("website")} placeholder="https://" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#999999]">Biggest content challenge</label>
                    <Select value={form.challenge} onValueChange={(v) => setForm((f) => ({ ...f, challenge: v }))}>
                      <SelectTrigger data-testid="select-challenge" className="mt-3 rounded-none border-0 border-b border-[#111111] px-1 py-6 text-base focus:ring-0 shadow-none">
                        <SelectValue placeholder="Select the one that hurts most" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {CHALLENGE_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o} data-testid={`challenge-${o.slice(0, 8)}`} className="rounded-none">
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#999999]">
                      {intent === "sample" ? "Anything specific you'd like to see in the sample?" : "Tell us more"}
                    </label>
                    <textarea data-testid="input-message" rows={3} className="editorial-input mt-2 resize-none" value={form.message} onChange={set("message")} placeholder="Optional" />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 bg-[#111111] text-white font-semibold px-8 py-4 hover:bg-[#333333] transition-colors group disabled:opacity-60"
                  >
                    {loading ? "Sending…" : intent === "sample" ? "Request a Sample Article" : "Send Inquiry"}
                    <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
